import React, { useState } from "react";
import {
  EuiButton,
  EuiButtonIcon,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSelect,
  EuiSpacer,
  EuiSwitch,
  EuiText,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { OplAttendeeInput } from "./api";

const i18n = "anketa.opsanio.opl";

/**
 * Editing who was in the round.
 *
 * A participant is a person, not an account: the name is the only required
 * field. Customer staff, external specialists and stand-ins usually have no
 * login here, and they still take part, still own points and still belong in
 * the minutes. An address is optional and only decides one thing - whether the
 * summary mail can reach them.
 */

export interface ParticipantEditorProps {
  value: OplAttendeeInput[];
  onChange: (next: OplAttendeeInput[]) => void;
  /** Addresses already known for this appliance, offered as quick adds. */
  suggestions?: Array<{ value: string; label: string }>;
  /**
   * Identities the server adds to every round regardless. Removal is disabled
   * for them here rather than letting the row be deleted and silently reappear
   * on save - it can still be removed by editing the round afterwards.
   */
  lockedKeys?: string[];
}

const blank = (): OplAttendeeInput => ({
  name: "",
  email: "",
  present: true,
  isGuest: false,
});

export const ParticipantEditor = ({
  value,
  onChange,
  suggestions = [],
  lockedKeys = [],
}: ParticipantEditorProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const t = (k: string, d: string) =>
    translate(`${i18n}.${k}`, { defaultMessage: d });

  const [pick, setPick] = useState("");

  const patch = (index: number, next: Partial<OplAttendeeInput>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...next } : row)));

  const remove = (index: number) =>
    onChange(value.filter((_, i) => i !== index));

  const unused = suggestions.filter(
    (s) => !value.some((v) => (v.email ?? "").toLowerCase() === s.value.toLowerCase()),
  );

  const withoutMail = value.filter((v) => !(v.email ?? "").trim()).length;

  const locked = new Set(lockedKeys.map((k) => k.toLowerCase()));
  const isLocked = (row: OplAttendeeInput) =>
    locked.has((row.email ?? "").trim().toLowerCase()) ||
    locked.has(row.name.trim().toLowerCase());

  return (
    <>
      {value.length === 0 && (
        <EuiText size="xs" color="subdued">
          {t("participants.none", "Noch keine Teilnehmer erfasst.")}
        </EuiText>
      )}

      {value.map((row, index) => {
        const absent = row.present === false;
        return (
          <EuiPanel
            key={index}
            paddingSize="s"
            hasShadow={false}
            hasBorder
            style={{ marginBottom: 8 }}
          >
            <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false} wrap>
              <EuiFlexItem style={{ minWidth: 150 }}>
                <EuiFieldText
                  compressed
                  fullWidth
                  placeholder={t("participants.name", "Name")}
                  aria-label={t("participants.name", "Name")}
                  value={row.name}
                  isInvalid={row.name.trim() === ""}
                  onChange={(e) => patch(index, { name: e.target.value })}
                />
              </EuiFlexItem>
              <EuiFlexItem style={{ minWidth: 170 }}>
                <EuiFieldText
                  compressed
                  fullWidth
                  placeholder={t("participants.emailOptional", "E-Mail (optional)")}
                  aria-label={t("participants.emailOptional", "E-Mail (optional)")}
                  value={row.email ?? ""}
                  onChange={(e) => patch(index, { email: e.target.value })}
                />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiSwitch
                  compressed
                  label={t("present", "Anwesend")}
                  checked={row.present !== false}
                  // Guests are always present in the round they are listed for.
                  disabled={row.isGuest === true}
                  onChange={(e) =>
                    patch(index, {
                      present: e.target.checked,
                      ...(e.target.checked
                        ? { absenceReason: undefined, substitute: "" }
                        : {}),
                    })
                  }
                />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiSwitch
                  compressed
                  label={t("guest", "Gast")}
                  checked={row.isGuest === true}
                  // A guest who is not there is not a guest of that round - you
                  // remove them rather than mark them absent. Marking someone a
                  // guest therefore also puts them back to present, so the pair
                  // of switches cannot produce a participant nobody can see.
                  onChange={(e) =>
                    patch(index, {
                      isGuest: e.target.checked,
                      ...(e.target.checked
                        ? { present: true, absenceReason: undefined, substitute: "" }
                        : {}),
                    })
                  }
                />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButtonIcon
                  iconType="trash"
                  color="danger"
                  aria-label={t("delete", "Löschen")}
                  disabled={isLocked(row)}
                  title={
                    isLocked(row)
                      ? t(
                          "participants.lockedHint",
                          "Standard-Kontakt der Appliance - wird jeder Runde hinzugefügt. Nach dem Anlegen entfernbar.",
                        )
                      : t("delete", "Löschen")
                  }
                  onClick={() => remove(index)}
                />
              </EuiFlexItem>
            </EuiFlexGroup>

            {/* Guests carry an organisation; absentees carry a reason and a
                substitute. Neither row is shown when it cannot apply. */}
            {row.isGuest && (
              <>
                <EuiSpacer size="xs" />
                <EuiFieldText
                  compressed
                  fullWidth
                  placeholder={t("participants.organisation", "Organisation oder Rolle")}
                  aria-label={t("participants.organisation", "Organisation oder Rolle")}
                  value={row.organisation ?? ""}
                  onChange={(e) => patch(index, { organisation: e.target.value })}
                />
              </>
            )}

            {absent && !row.isGuest && (
              <>
                <EuiSpacer size="xs" />
                <EuiFlexGroup gutterSize="s" responsive={false} wrap>
                  <EuiFlexItem grow={false} style={{ minWidth: 170 }}>
                    <EuiSelect
                      compressed
                      value={row.absenceReason ?? "excused"}
                      options={[
                        { value: "excused", text: t("absence.excused", "entschuldigt") },
                        {
                          value: "unexcused",
                          text: t("absence.unexcused", "unentschuldigt"),
                        },
                      ]}
                      onChange={(e) =>
                        patch(index, {
                          absenceReason: e.target.value as "excused" | "unexcused",
                        })
                      }
                      aria-label={t("absent", "Abwesend")}
                    />
                  </EuiFlexItem>
                  <EuiFlexItem style={{ minWidth: 170 }}>
                    <EuiFieldText
                      compressed
                      fullWidth
                      placeholder={t("participants.substitute", "Vertretung (optional)")}
                      aria-label={t("participants.substitute", "Vertretung (optional)")}
                      value={row.substitute ?? ""}
                      onChange={(e) => patch(index, { substitute: e.target.value })}
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </>
            )}

            {!row.isGuest && row.present !== false && (
              <>
                <EuiSpacer size="xs" />
                <EuiFlexGroup gutterSize="s" responsive={false} wrap>
                  <EuiFlexItem grow={false} style={{ maxWidth: 140 }}>
                    <EuiFieldText
                      compressed
                      placeholder={t("participants.from", "ab (10:20)")}
                      aria-label={t("participants.from", "ab")}
                      value={row.fromTime ?? ""}
                      onChange={(e) => patch(index, { fromTime: e.target.value })}
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false} style={{ maxWidth: 140 }}>
                    <EuiFieldText
                      compressed
                      placeholder={t("participants.until", "bis (10:45)")}
                      aria-label={t("participants.until", "bis")}
                      value={row.untilTime ?? ""}
                      onChange={(e) => patch(index, { untilTime: e.target.value })}
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </>
            )}
          </EuiPanel>
        );
      })}

      <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false} wrap>
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            iconType="plusInCircle"
            onClick={() => onChange([...value, blank()])}
          >
            {t("participants.add", "Teilnehmer hinzufügen")}
          </EuiButton>
        </EuiFlexItem>
        {unused.length > 0 && (
          <EuiFlexItem grow={false} style={{ minWidth: 220 }}>
            <EuiSelect
              compressed
              hasNoInitialSelection
              value={pick}
              options={unused.map((s) => ({ value: s.value, text: s.label }))}
              onChange={(e) => {
                const hit = unused.find((s) => s.value === e.target.value);
                if (hit) {
                  onChange([
                    ...value,
                    {
                      name: hit.label,
                      email: hit.value,
                      present: true,
                      isGuest: false,
                      source: "contact",
                    },
                  ]);
                }
                setPick("");
              }}
              aria-label={t("participants.fromContacts", "Aus Kontakten übernehmen")}
            />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      {lockedKeys.length > 0 && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            size="s"
            color="primary"
            iconType="iInCircle"
            title={t(
              "participants.defaultsNote",
              "Die Standard-Kontakte der Appliance sind in jeder Runde enthalten. Für eine einzelne Runde lassen sie sich nach dem Anlegen entfernen.",
            )}
          />
        </>
      )}

      {withoutMail > 0 && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            size="s"
            color="primary"
            iconType="iInCircle"
            title={t(
              "participants.noMailNote",
              "Teilnehmer ohne E-Mail-Adresse nehmen voll teil und können Punkte übernehmen - sie erhalten nur das Protokoll nicht.",
            )}
          />
        </>
      )}
    </>
  );
};
